import { createSelector } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { selectScanDate } from "slices/writing/scoring/analysis/selectors";
import { selectIsDocumentSyncing } from "slices/writing/selectors";

import { contentAdapter } from "./currentDocumentSlice";
import { getHeadingLevel, isHeadingItem } from "./heading";

export const selectCurrentDocState = (state) => state?.addIn?.currentDocument;

export const selectIsListening = createSelector(
  [selectCurrentDocState],
  (currentDocument) => Boolean(currentDocument.isListening)
);

export const selectIsReanalyzing = createSelector(
  [selectCurrentDocState],
  (currentDocument) => Boolean(currentDocument.isReanalyzing)
);

// isSyncing covers only the backend flow; isReanalyzing spans the upload and the
// follow-up readability fetch either side of it.
export const selectIsAnalysisInProgress = createSelector(
  [selectIsDocumentSyncing, selectIsReanalyzing],
  (isSyncing, isReanalyzing) => Boolean(isSyncing) || isReanalyzing
);

export const selectIsOutlineSyncPending = createSelector(
  [selectCurrentDocState],
  (currentDocument) => Boolean(currentDocument.isOutlineSyncPending)
);

export const selectLastEditedAt = createSelector(
  [selectCurrentDocState],
  (currentDocument) => currentDocument.lastEditedAt
);

export const selectLastSavedAt = createSelector(
  [selectCurrentDocState],
  (currentDocument) => currentDocument.lastSavedAt ?? null
);

export const selectIsSaving = createSelector(
  [selectCurrentDocState],
  (currentDocument) => Boolean(currentDocument.isSaving)
);

export const selectPresenceInstanceId = createSelector(
  [selectCurrentDocState],
  (currentDocument) => currentDocument.presenceInstanceId
);

export const selectFormattedLastSavedAt = createSelector(
  [selectLastSavedAt],
  (lastSavedAt) =>
    lastSavedAt ? format(new Date(lastSavedAt), "M.d.yyyy 'at' H:mm") : null
);

export const selectContentState = createSelector(
  [selectCurrentDocState],
  (currentDocument) => currentDocument.content
);

const adapterSelectors = contentAdapter.getSelectors(
  (state) => state.addIn.currentDocument.content
);

// Ordered array of all block-level content items in document order.
export const selectAllContentItems = adapterSelectors.selectAll;

// Look up a single item by its uniqueLocalId.
export const selectContentItemById = adapterSelectors.selectById;

// All heading content items, in document order. Classification is language
// independent — see `isHeadingItem` (styleBuiltIn enum + outlineLevel), never
// the localized `style` display name.
export const selectHeadingContentItems = createSelector(
  [selectAllContentItems],
  (items) => items.filter(isHeadingItem)
);

export const selectHeadingsAsOutlineItems = createSelector(
  [selectHeadingContentItems],
  (headings) => {
    if (!headings?.length) return [];

    return headings.map((item) => ({
      headingLevel: getHeadingLevel(item),
      id: item.id, // < this is the DOCX uniqueLocalId
      outlineItemId: null,
      title: item.text,
    }));
  }
);

export const selectContentItemCount = createSelector(
  [selectContentState],
  (content) => content.ids.length
);

export const selectDocumentHasContent = createSelector(
  [selectAllContentItems],
  (items) => items.some((item) => item.text?.trim().length > 0)
);

export const selectCurrentParagraph = createSelector(
  [selectCurrentDocState],
  (currentDocument) =>
    currentDocument.content.entities[currentDocument.currentParagraphId] ?? null
);

export const selectIsScoringStale = createSelector(
  [selectLastEditedAt, selectScanDate],
  (lastEditedDocument, scanDate) => {
    if (!scanDate) return true;

    // If no found date, simply use Epoch to give Scoring primacy.
    const lastEditedDate = new Date(lastEditedDocument ?? 1);

    return lastEditedDate > scanDate;
  }
);
